"use client";

import { useEffect, useState } from "react";

/**
 * Tells the visitor their tab is running a superseded build, instead of
 * letting a save die behind the browser's own "This page couldn't load".
 *
 * WHY A FETCH WRAPPER AND NOT `error.tsx`: when a page submits a Server
 * Action whose id no longer exists in the running deployment, Next answers
 * with a bare **HTTP 404**, `content-type: text/plain`, body "Server action
 * not found.", and the header this component watches for. That is a transport
 * failure, not a React render error — no error boundary is ever invoked, so
 * `error.tsx` / `global-error.tsx` cannot catch it. Reading the response
 * header is the only reliable client-side signal.
 *
 * Every deploy creates this window: any tab opened beforehand keeps the old
 * action ids, so the next save or form submit 404s. It hits the admin article
 * editor hardest (long sessions, unsaved work) but public form submits fail
 * the same way — the runtime log shows bursts of these immediately after each
 * deploy, which are real visitors whose form submission silently failed.
 *
 * DELIBERATELY DOES NOT AUTO-RELOAD. The unsaved work only exists in this
 * tab's memory; reloading to "fix" it is what destroys it. Surface the cause,
 * tell them to copy anything they need, and let them choose.
 */
export function StaleDeploymentNotice() {
  const [stale, setStale] = useState(false);

  useEffect(() => {
    const original = window.fetch;
    // Pass-through wrapper: it only ever READS a response header and never
    // alters, buffers, or swallows the response.
    window.fetch = async function patched(...args) {
      const res = await original.apply(this, args);
      try {
        if (res.headers.get("x-nextjs-action-not-found")) setStale(true);
      } catch {
        /* opaque/CORS responses expose no headers — ignore */
      }
      return res;
    };
    return () => {
      window.fetch = original;
    };
  }, []);

  if (!stale) return null;

  return (
    <div
      role="alertdialog"
      aria-live="assertive"
      aria-label="The site was updated"
      /* z-[300] clears the cookie-consent layer, which is z-[200] and renders
         `inset-0` full-screen on mobile — at any lower z-index this notice is
         simply invisible on a phone whenever consent hasn't been given yet,
         which is exactly when a visitor is least able to explain the failure. */
      className="fixed inset-x-0 bottom-0 z-[300] border-t-4 border-gold-400 bg-navy-800 px-5 py-4 text-white shadow-floating sm:inset-x-auto sm:right-5 sm:bottom-5 sm:max-w-md sm:rounded-xl sm:border-t-0 sm:border-l-4"
    >
      <p className="font-serif text-lg leading-snug">The site was updated</p>
      <p className="mt-1.5 text-[13px] leading-relaxed text-white/85">
        This tab is still running the previous version, so saving and form
        submissions won&rsquo;t go through until you reload.{" "}
        <strong className="font-semibold text-white">
          Anything you&rsquo;ve typed is still on screen but not yet saved —
          copy it somewhere first,
        </strong>{" "}
        because reloading will discard it.
      </p>
      <div className="mt-3 flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="rounded-lg bg-white px-4 py-2 text-[13px] font-semibold text-navy-800 hover:bg-cream-100"
        >
          Reload now
        </button>
        <button
          type="button"
          onClick={() => setStale(false)}
          className="rounded-lg border border-white/40 px-4 py-2 text-[13px] font-semibold text-white hover:bg-white/10"
        >
          Not yet
        </button>
      </div>
    </div>
  );
}
